import { useErpProviders } from '@euler/app/flows/erp/functions/useErpProviders';
import { showErpToast } from '@euler/app/flows/erp/utils/showErpToast';
import { Button } from '@euler/components/Button';
import { Colors, StatusColors } from '@euler/components/Colors';
import { Input } from '@euler/components/form/Input';
import { FontFamily } from '@euler/components/typography';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ErpHostedLoginStatus } from '@euler/model/erp-integrations';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useEffect, useMemo, useState } from 'react';
import { Keyboard, ScrollView, Text, View } from 'react-native';

export const ErpLoginScreen = wrapNavigatorScreen(
  () => {
    return <Content />;
  },
  {
    title: '登录',
  },
);

const Content = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const { getProvider, getProviderName } = useErpProviders();
  const providerId = route.params?.providerId;
  const provider = getProvider(providerId);
  const providerName = getProviderName(
    providerId,
    route.params?.providerName || '当前服务商',
  );
  // Which generations exist, and what they are called, comes from the
  // catalog; the app no longer knows that Casstime and F6 are the versioned
  // ones or that their generations are named V1 and V2.
  const versionOptions = provider?.versions ?? [];
  const versionRequired = versionOptions.length > 1;

  const { storeErpIntegrationService } = useServiceFactory();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [version, setVersion] = useState<string | undefined>(
    route.params?.providerVersion,
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const canSubmit =
    !isSubmitting &&
    provider != null &&
    Boolean(username) &&
    Boolean(password) &&
    (!versionRequired || Boolean(version));

  const statusTone = useMemo(() => {
    if (!status) {
      return '#1890ff';
    }
    if (status.includes('失败')) {
      return StatusColors.Danger;
    }
    if (status.includes('成功')) {
      return StatusColors.Success;
    }
    return '#1890ff';
  }, [status]);

  useEffect(() => {
    navigation.setOptions({
      title: `登录${providerName}`,
    });
  }, [navigation, providerName]);

  useEffect(() => {
    setVersion(route.params?.providerVersion);
  }, [providerId, route.params?.providerVersion]);

  const onSubmit = usePersistFn(async () => {
    if (!username || !password) {
      showErpToast(`请输入${providerName}账号和密码`, 'error');
      return;
    }
    if (versionRequired && !version) {
      showErpToast(`请选择${providerName}版本`, 'error');
      return;
    }
    try {
      Keyboard.dismiss();
      setIsSubmitting(true);
      setStatus(`正在提交${providerName}登录请求...`);

      const credentials: any = { username, password };

      const res = await storeErpIntegrationService.submitErpLoginCredential(
        providerId,
        credentials,
        versionRequired ? version : undefined,
      );

      if (res?.sessionId) {
        setSessionId(res.sessionId);
        setStatus(`正在自动登录${providerName}，请稍候...`);
      } else {
        showErpToast('提交失败，未获取到登录会话ID', 'error');
        setIsSubmitting(false);
        setStatus('');
      }
    } catch (e: any) {
      setIsSubmitting(false);
      setStatus('');
      showErpToast(e.message || '请求失败', 'error');
    }
  });

  useEffect(() => {
    if (!sessionId) return;

    let timeout: ReturnType<typeof setTimeout> | undefined = undefined;
    const checkStatus = async () => {
      try {
        const data =
          await storeErpIntegrationService.getErpLoginStatus(sessionId);

        if (data?.status === ErpHostedLoginStatus.Success) {
          // The server creates the integration as part of the login, so
          // there is nothing left to save from here.
          setStatus(`${providerName}绑定成功！`);
          setIsSubmitting(false);
          setSessionId(null);
          navigation.reset({
            index: 0,
            routes: [{ name: '_erpManagement' }],
          });
          showErpToast(`${providerName}绑定成功`, 'success');
        } else if (data?.status === ErpHostedLoginStatus.Failed) {
          const reason = data?.error || '未知原因';
          setStatus(`${providerName}绑定失败：` + reason);
          setIsSubmitting(false);
          setSessionId(null);
          showErpToast(`${providerName}绑定失败：` + reason, 'error');
        } else {
          setStatus(`${providerName}正在执行登录授权，请稍候...`);
          timeout = setTimeout(checkStatus, 3000);
        }
      } catch (e: any) {
        setStatus('状态查询失败');
        setIsSubmitting(false);
        setSessionId(null);
        showErpToast('状态查询失败：' + e.message, 'error');
      }
    };

    timeout = setTimeout(checkStatus, 3000);
    return () => clearTimeout(timeout);
  }, [
    sessionId,
    navigation,
    providerId,
    providerName,
    storeErpIntegrationService,
    version,
  ]);

  return (
    <ScrollView
      css="flex: 1; background-color: #fff; padding: 20px;"
      keyboardShouldPersistTaps="handled"
      automaticallyAdjustKeyboardInsets
    >
      <View
        css={`
          background-color: #f6faff;
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 16px;
        `}
      >
        <Text
          css={`
            font-family: ${FontFamily.NotoSans.Medium};
            font-size: 15px;
            color: #2669db;
            margin-bottom: 6px;
          `}
        >
          接入指引
        </Text>
        <Text css="font-size: 13px; color: #4a4a4a; line-height: 21px;">
          1. 输入 {providerName} 账号密码完成授权
        </Text>
        <Text css="font-size: 13px; color: #4a4a4a; line-height: 21px;">
          2. 登录成功后，建议立即完成员工绑定
        </Text>
        <Text css="font-size: 13px; color: #4a4a4a; line-height: 21px;">
          3. 员工绑定完成后，再进入订单同步页开始使用
        </Text>
      </View>

      <View css="margin-bottom: 16px;">
        <Text
          css={`
            font-family: ${FontFamily.NotoSans.Bold};
            font-size: 24px;
            color: #333;
            margin-bottom: 10px;
          `}
        >
          连接{providerName}
        </Text>
        <Text css="font-size: 14px; color: #666;">
          {`请输入您的${providerName}账号密码进行授权绑定`}
        </Text>
        {versionOptions.length > 1 && (
          <Text css="font-size: 12px; color: #999; margin-top: 8px; line-height: 18px;">
            不同版本对应不同的 ERP
            接口协议；如果当前版本登录失败，可尝试切换另一版本重新授权。
          </Text>
        )}
      </View>

      <View css="margin-bottom: 24px;">
        <Text
          css={`
            font-family: ${FontFamily.NotoSans.Medium};
            margin-bottom: 8px;
            color: #333;
            font-size: 15px;
          `}
        >
          账号
        </Text>
        <Input
          value={username}
          placeholder={`请输入${providerName}账号`}
          onChangeText={setUsername}
          editable={!isSubmitting}
          autoCapitalize="none"
          autoCorrect={false}
          containerStyle={{
            marginBottom: 16,
            backgroundColor: '#f8f9fa',
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#e4e7eb',
          }}
          style={{ paddingHorizontal: 12, height: 48 }}
        />

        <Text
          css={`
            font-family: ${FontFamily.NotoSans.Medium};
            margin-bottom: 8px;
            color: #333;
            font-size: 15px;
          `}
        >
          密码
        </Text>
        <Input
          value={password}
          placeholder={`请输入${providerName}密码`}
          onChangeText={setPassword}
          secureTextEntry
          editable={!isSubmitting}
          containerStyle={{
            marginBottom: 16,
            backgroundColor: '#f8f9fa',
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#e4e7eb',
          }}
          style={{ paddingHorizontal: 12, height: 48 }}
        />

        {versionOptions.length > 1 && (
          <>
            <Text
              css={`
                font-family: ${FontFamily.NotoSans.Medium};
                margin-bottom: 8px;
                color: #333;
                font-size: 15px;
              `}
            >
              {`${providerName}版本（必选）`}
            </Text>
            <View css="flex-direction: row; margin-bottom: 16px;">
              {versionOptions.map((option, index) => {
                const active = version === option.code;
                return (
                  <Button
                    key={option.code}
                    title={option.name}
                    onPress={() => setVersion(option.code)}
                    style={{
                      flex: 1,
                      marginRight: index < versionOptions.length - 1 ? 10 : 0,
                      backgroundColor: active ? '#e6f7ff' : '#f8f9fa',
                      borderColor: active ? '#1890ff' : '#e4e7eb',
                      borderWidth: 1,
                      borderRadius: 8,
                      height: 40,
                    }}
                    textStyle={{
                      color: active ? '#1890ff' : '#666',
                      fontWeight: active ? 'bold' : 'normal',
                    }}
                  />
                );
              })}
            </View>
          </>
        )}
      </View>

      {status ? (
        <View
          css={`
            background-color: #f8fafc;
            border-radius: 14px;
            padding: 14px 16px;
            margin-bottom: 20px;
            border-width: 1px;
            border-color: #e6eef7;
          `}
        >
          <Text
            css={`
              font-size: 14px;
              color: ${statusTone};
              line-height: 21px;
            `}
          >
            {status}
          </Text>
          {!status.includes('失败') && (
            <Text css="font-size: 12px; color: #98a2b3; margin-top: 6px; line-height: 18px;">
              授权过程中请勿重复提交，页面会自动刷新当前状态。
            </Text>
          )}
        </View>
      ) : null}

      <Button
        title={
          isSubmitting
            ? `正在连接${providerName}...`
            : `确认连接${providerName}`
        }
        onPress={onSubmit}
        disabled={!canSubmit}
        style={{
          borderRadius: 8,
          marginTop: 10,
          backgroundColor: canSubmit ? '#5ba0e0' : Colors.Gray4,
          height: 48,
        }}
        textStyle={{ color: '#fff', fontSize: 16, fontWeight: 'bold' }}
      />
    </ScrollView>
  );
};
